import { forwardRef, useState, type ComponentPropsWithoutRef, type ReactNode } from 'react';
import * as stylex from '@stylexjs/stylex';
import { ButtonPrimitive, Text } from '@figma/fpl-components';
import { borderRadius, color, spacer } from '@figma/fpl-tokens/tokens.stylex';
import { Tooltip, type TooltipProps } from '@/UI/Tooltip/Tooltip';

export type MenuPlacement = 'bottom span-right' | 'top span-right';

/** Ghost-style menu trigger with secondary text and icon. FPL `Button` does not allow recoloring. */
export const MenuTrigger = forwardRef<
  HTMLButtonElement,
  Omit<ComponentPropsWithoutRef<typeof ButtonPrimitive>, 'className' | 'style'> & { icon?: ReactNode }
>(function MenuTrigger({ icon, children, ...props }, ref) {
  return (
    <ButtonPrimitive {...props} ref={ref} {...stylex.props(styles.trigger, !icon && styles.triggerNoIcon)}>
      {icon && (
        <span aria-hidden {...stylex.props(styles.triggerIcon)}>
          {icon}
        </span>
      )}
      <Text.span variant="body-medium" {...stylex.props(styles.triggerLabel)}>
        {children}
      </Text.span>
    </ButtonPrimitive>
  );
});

/**
 * The hover name of a menu trigger. It stays closed while the trigger's menu is open, so it doesn't sit under
 * the menu. The tooltip is always controlled: switching it between modes would remount the trigger the menu
 * is anchored to.
 */
export function MenuTriggerTooltip({
  title,
  menuOpen,
  children,
}: {
  title: string;
  menuOpen: boolean;
  children: TooltipProps['children'];
}) {
  const [hovered, setHovered] = useState(false);
  // A closed tooltip doesn't report the pointer leaving. Without this, it would show again when the menu closes,
  // even with the pointer already gone.
  if (menuOpen && hovered) {
    setHovered(false);
  }
  return (
    <Tooltip
      title={title}
      open={hovered && !menuOpen}
      onOpen={() => setHovered(true)}
      onClose={() => setHovered(false)}
    >
      {children}
    </Tooltip>
  );
}

const styles = stylex.create({
  trigger: {
    '--fpl-icon-color': color.iconSecondary,
    alignItems: 'center',
    backgroundColor: {
      default: 'transparent',
      ':hover': color.bgTransparentHover,
      ':active': color.bgTransparentPressed,
    },
    borderRadius: borderRadius.medium,
    borderStyle: 'none',
    color: color.textSecondary,
    cursor: 'default',
    display: 'inline-flex',
    flexShrink: 0,
    height: '24px',
    // ButtonPrimitive is unstyled and global CSS clears native outlines.
    outline: {
      default: 'none',
      ':focus-visible': `1px solid ${color.borderSelected}`,
    },
    outlineOffset: '-1px',
    paddingBlock: 0,
    paddingLeft: 0,
    paddingRight: spacer.spacer2,
  },
  triggerNoIcon: {
    paddingLeft: spacer.spacer2,
  },
  triggerIcon: {
    display: 'inline-flex',
    flexShrink: 0,
  },
  triggerLabel: {
    alignItems: 'center',
    display: 'inline-flex',
    gap: '2px',
    whiteSpace: 'nowrap',
  },
});
